import { BusinessRounded, Check, PersonOutlineRounded, WorkOutlineRounded } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { portalLandingPath } from '../auth/portalRouting';
import { hasEmployeePortalAccess, PortalContextKind, PortalContextSelection } from '../models/portal-context';

export interface PortalContextSwitcherProps {
    onNavigate?: () => void;
}

const PortalContextSwitcher = ({ onNavigate }: PortalContextSwitcherProps) => {
    const history = useHistory();
    const { accessInfo, changePortalContext } = useAccessInfo();
    const [switching, setSwitching] = useState(false);
    const isActiveProducer = (producer) => producer.AccessState === 'Active';
    const hasEmployeePortal = hasEmployeePortalAccess(accessInfo.portalAccess);
    const workspaces = accessInfo.portalAccess?.Workspaces ?? [];
    const producers = accessInfo.portalAccess?.Producers ?? [];
    const selectableProducerCount = producers.filter(isActiveProducer).length;
    const contextCount = (hasEmployeePortal ? 1 : 0) + workspaces.length + selectableProducerCount;

    if (contextCount <= 1)
        return null;

    const selectContext = async (context: PortalContextSelection) => {
        if (JSON.stringify(context) === JSON.stringify(accessInfo.currentPortalContext)) {
            onNavigate?.();
            return;
        }

        setSwitching(true);
        try {
            await changePortalContext(context);
            onNavigate?.();
            history.replace(portalLandingPath(context));
        } finally {
            setSwitching(false);
        }
    };

    return <>
        <Typography
            component="div"
            variant="overline"
            color="text.secondary"
            sx={{ px: 2, pt: 1, fontWeight: 800 }}
        >
            Switch portal
        </Typography>
        {hasEmployeePortal && <MenuItem
            disabled={switching}
            selected={accessInfo.currentPortalContext?.Kind === PortalContextKind.Employee}
            aria-current={accessInfo.currentPortalContext?.Kind === PortalContextKind.Employee ? 'page' : undefined}
            onClick={() => selectContext({ Kind: PortalContextKind.Employee })}
        >
            <ListItemIcon>
                {accessInfo.currentPortalContext?.Kind === PortalContextKind.Employee
                    ? <Check color="primary" />
                    : <PersonOutlineRounded />}
            </ListItemIcon>
            <ListItemText
                primary="Personal development"
                secondary="Your employee development portal"
            />
        </MenuItem>}
        {workspaces.map(workspace => {
            const active = accessInfo.currentPortalContext?.Kind === PortalContextKind.Workspace
                && accessInfo.currentPortalContext.OrganizationId === workspace.Id;
            return <MenuItem
                key={workspace.Id}
                disabled={switching}
                selected={active}
                aria-current={active ? 'page' : undefined}
                onClick={() => selectContext({
                    Kind: PortalContextKind.Workspace,
                    OrganizationId: workspace.Id,
                })}
            >
                <ListItemIcon>{active ? <Check color="primary" /> : <WorkOutlineRounded />}</ListItemIcon>
                <ListItemText
                    primary={`${workspace.Name} workspace`}
                    secondary="Company workspace"
                />
            </MenuItem>;
        })}
        {producers.map(producer => {
            const isSelectableProducer = isActiveProducer(producer);
            const active = accessInfo.currentPortalContext?.Kind === PortalContextKind.Producer
                && accessInfo.currentPortalContext.ProducerCompanyId === producer.Id;
            return <MenuItem
                key={producer.Id}
                disabled={switching || !isSelectableProducer}
                selected={active}
                aria-current={active ? 'page' : undefined}
                onClick={() => isSelectableProducer
                    ? selectContext({
                        Kind: PortalContextKind.Producer,
                        ProducerCompanyId: producer.Id,
                    })
                    : undefined}
            >
                <ListItemIcon>{active ? <Check color="primary" /> : <BusinessRounded />}</ListItemIcon>
                <ListItemText
                    primary={`${producer.Name} company`}
                    secondary={`${producer.Role} • ${producer.AccessState}` + (isSelectableProducer ? '' : ' (inactive)')}
                />
            </MenuItem>;
        })}
        <Divider sx={{ my: 1 }} />
    </>;
};

export default PortalContextSwitcher;
